/*!
 * msk-announcement-bar.js — MSK · the corrupted-transmission announcement bar
 * --------------------------------------------------------------------------
 * Companion to sections/msk-announcement-bar.liquid. One line at a time
 * surfaces out of engraving-noise (decay), holds, may be "struck twice"
 * (same-ink clip slices), then decays away as the next line resolves in.
 *
 * Same grammar / glyph set / palette as msk-corruption.js — this is the
 * cycling driver the base library's fire-once data-msk-decay doesn't cover.
 * No dependencies, no new colors, no filters. Vanilla, Dawn-safe, idempotent.
 *
 * Source of truth for the copy is the visually-hidden <ul data-xbar-list>
 * inside each bar (also what assistive tech reads); the animated span is
 * aria-hidden. No-JS / reduced-motion => the server-rendered first line
 * simply stays put.
 */
(function () {
  'use strict';

  var GLYPHS = ".·:;+*#%@§†‡,'";
  var DECAY_MIN = 900, DECAY_MAX = 1400;   // resolve-in duration
  var DISSOLVE_MIN = 480, DISSOLVE_MAX = 720; // decay-away duration
  var SHUFFLE_MS = 48;                      // ~20fps glyph churn — reads as damage
  var RAMP = 0.72;                          // share of resolve spent on the L→R sweep
  var STRIKE_MS = 90;                       // struck-twice flash length
  var STRIKE_CHANCE = 0.55;                 // odds of a strike during a given hold

  function rand(a, b) { return a + Math.random() * (b - a); }
  function randGlyph() { return GLYPHS.charAt((Math.random() * GLYPHS.length) | 0); }
  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  var bars = []; // active controllers, for visibility + reduced-motion + teardown

  function Bar(root) {
    this.root = root;
    this.glyphs = root.querySelector('[data-xbar-text]');
    var list = root.querySelector('[data-xbar-list]');
    this.lines = list
      ? Array.prototype.map.call(list.querySelectorAll('li'), function (li) {
          return { text: (li.textContent || '').trim(), link: li.getAttribute('data-link') || '' };
        }).filter(function (l) { return l.text; })
      : [];
    this.hold = parseInt(root.getAttribute('data-hold'), 10) || 4500;
    this.strike = root.getAttribute('data-strike') === 'true';
    this.i = 0;
    this.token = 0;    // bumped on every phase change to cancel stale rAF chains
    this.raf = null;
    this.timers = [];
    this.stopped = false;
  }

  /* perf 2026-10-13: rewrite the one text node in place. Assigning textContent removes and
     re-creates the node 20x a second, and every one of those is a DOM tree change the
     browser answers by re-checking styles across the page. */
  Bar.prototype.setText = function (text) {
    var g = this.glyphs, n = g.firstChild;
    if (n && n.nodeType === 3 && !n.nextSibling) { if (n.nodeValue !== text) n.nodeValue = text; }
    else g.textContent = text;
  };

  Bar.prototype.clearTimers = function () {
    for (var k = 0; k < this.timers.length; k++) clearTimeout(this.timers[k]);
    this.timers = [];
  };

  Bar.prototype.later = function (fn, ms) {
    var id = setTimeout(fn, ms);
    this.timers.push(id);
    return id;
  };

  Bar.prototype.setLink = function (link) {
    // wrap/unwrap the glyph span in an <a> so a line can be clickable
    var span = this.glyphs;
    var parentA = span.parentNode && span.parentNode.tagName === 'A' ? span.parentNode : null;
    if (link) {
      if (parentA) { parentA.setAttribute('href', link); return; }
      var a = document.createElement('a');
      a.className = 'msk-xbar__link';
      a.setAttribute('href', link);
      span.parentNode.insertBefore(a, span);
      a.appendChild(span);
    } else if (parentA) {
      parentA.parentNode.insertBefore(span, parentA);
      parentA.parentNode.removeChild(parentA);
    }
  };

  Bar.prototype.showStatic = function (text) {
    this.token++;
    this.clearTimers();
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
    this.glyphs.classList.remove('msk-xbar--strike');
    this.setText(text);
    this.glyphs.setAttribute('data-txt', text);
  };

  // resolve OUT of noise, L→R with jitter; then cb()
  Bar.prototype.resolveIn = function (text, cb) {
    var self = this, myToken = ++this.token;
    this.setLink(this.lines[this.i].link);
    var chars = text.split('');
    var total = text.replace(/\s/g, '').length || 1;
    var dur = rand(DECAY_MIN, DECAY_MAX), seen = 0, settle = [];
    chars.forEach(function (ch) {
      if (/\s/.test(ch)) { settle.push(-1); }
      else { settle.push(Math.min(dur, (seen / total) * dur * RAMP + rand(0, dur * (1 - RAMP)))); seen++; }
    });
    this.glyphs.setAttribute('data-txt', text);
    var start = performance.now(), last = -1e9;
    function step(ts) {
      if (myToken !== self.token) return;
      var t = ts - start;
      if (t >= dur) { self.setText(text); if (cb) cb(); return; }
      if (ts - last >= SHUFFLE_MS - 8) {
        last = ts;
        var out = '';
        for (var k = 0; k < chars.length; k++) {
          var s = settle[k];
          out += (s < 0 || t >= s) ? chars[k] : randGlyph();
        }
        self.setText(out);
      }
      /* perf 2026-10-13: the glyphs only change every SHUFFLE_MS, so wake up then, not every frame */
      setTimeout(function () { if (myToken === self.token) self.raf = requestAnimationFrame(step); }, SHUFFLE_MS);
    }
    self.raf = requestAnimationFrame(step);
  };

  // fray back into noise, R→L, then blank; then cb()
  Bar.prototype.resolveOut = function (text, cb) {
    var self = this, myToken = ++this.token;
    var chars = text.split('');
    var dur = rand(DISSOLVE_MIN, DISSOLVE_MAX), start = performance.now(), last = -1e9;
    function step(ts) {
      if (myToken !== self.token) return;
      var t = (ts - start) / dur;
      if (t >= 1) { self.setText(''); if (cb) cb(); return; }
      if (ts - last >= SHUFFLE_MS - 8) {
        last = ts;
        var out = '';
        for (var k = 0; k < chars.length; k++) {
          if (/\s/.test(chars[k])) { out += chars[k]; continue; }
          var threshold = 1 - (k / chars.length); // right edge frays first
          out += (t > threshold * 0.9) ? (Math.random() < t ? randGlyph() : ' ') : chars[k];
        }
        self.setText(out);
      }
      /* perf 2026-10-13: the glyphs only change every SHUFFLE_MS, so wake up then, not every frame */
      setTimeout(function () { if (myToken === self.token) self.raf = requestAnimationFrame(step); }, SHUFFLE_MS);
    }
    self.raf = requestAnimationFrame(step);
  };

  // white swell fired once each line is fully formed. Restarts the animation
  // per line (reflow) and no-ops under reduced motion (it's motion + luminance).
  Bar.prototype.pulseGlow = function () {
    if (reduced() || this.stopped) return;
    var g = this.glyphs;
    g.classList.remove('msk-xbar--ignite');
    void g.offsetWidth; // force reflow so the animation replays every line
    var done = function () {
      g.classList.remove('msk-xbar--ignite');
      g.removeEventListener('animationend', done);
    };
    g.addEventListener('animationend', done);
    this.later(done, 2200); // safety net if animationend is missed
    g.classList.add('msk-xbar--ignite');
  };

  Bar.prototype.maybeStrike = function () {
    if (!this.strike || reduced() || this.stopped) return;
    var self = this;
    this.glyphs.classList.add('msk-xbar--strike');
    this.later(function () { self.glyphs.classList.remove('msk-xbar--strike'); }, STRIKE_MS);
  };

  Bar.prototype.cycle = function () {
    if (this.stopped) return;
    var self = this;
    if (reduced() || this.lines.length === 0) {
      this.showStatic(this.lines.length ? this.lines[0].text : '');
      return;
    }
    var text = this.lines[this.i].text;
    this.resolveIn(text, function () {
      if (self.stopped) return;
      self.pulseGlow(); // white swell every time a line finishes forming
      // during the hold, maybe strike once
      if (Math.random() < STRIKE_CHANCE) self.later(function () { self.maybeStrike(); }, rand(1400, self.hold - 900));
      self.later(function () {
        if (self.stopped) return;
        self.resolveOut(text, function () {
          if (self.stopped) return;
          self.i = (self.i + 1) % self.lines.length;
          self.cycle();
        });
      }, self.hold);
    });
  };

  Bar.prototype.start = function () {
    this.stopped = false;
    if (reduced()) { this.showStatic(this.lines.length ? this.lines[0].text : ''); return; }
    this.cycle();
  };

  Bar.prototype.pause = function () {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
    this.clearTimers();
  };

  Bar.prototype.resume = function () {
    if (this.stopped || reduced()) return;
    // re-resolve the current line rather than trying to splice back into a stale phase
    this.token++;
    this.cycle();
  };

  Bar.prototype.destroy = function () {
    this.stopped = true;
    this.token++;
    this.pause();
  };

  function initRoot(scope) {
    var nodes = (scope || document).querySelectorAll('[data-msk-xbar]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.__mskXbar) continue;
      var bar = new Bar(el);
      el.__mskXbar = bar;
      bars.push(bar);
      bar.start();
    }
  }

  function teardown(scope) {
    if (!scope || scope.nodeType !== 1) return;
    for (var i = bars.length - 1; i >= 0; i--) {
      var b = bars[i];
      if (scope.contains(b.root)) {
        b.destroy();
        if (b.root.__mskXbar) delete b.root.__mskXbar;
        bars.splice(i, 1);
      }
    }
  }

  // pause everything when the tab is hidden (matches msk-corruption behavior)
  document.addEventListener('visibilitychange', function () {
    bars.forEach(function (b) { if (document.hidden) b.pause(); else b.resume(); });
  });

  // live flip to/from reduced motion
  if (window.matchMedia) {
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onChange = function () {
      bars.forEach(function (b) {
        if (reduced()) { b.showStatic(b.lines.length ? b.lines[0].text : ''); }
        else { b.token++; b.cycle(); }
      });
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) { initRoot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });

  function boot() { initRoot(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  window.MSKAnnouncementBar = { init: initRoot, version: '1.0.0' };
})();
